import { createAsyncThunk, createSelector, createSlice } from "@reduxjs/toolkit";
import deepmerge from "deepmerge";
import { useMemo } from "react";
import { useSelector } from "react-redux";
import { aggregatingFetch } from "../../common/hooks/networkHook";
import { logout } from "../../common/reducers/initContext";
import { createMapSelector, dataHookWithCredentialsFactory, DefaultFailureAction, DefaultSuccessAction, initMapFactory, initMultiMapFactory, queryMapFactory } from "../../common/reducers/utilities";
import { ensureArray } from "../../common/utils/arrayUtilities";
import { dateRangeOverlap, maxDate, minDate } from "../../common/utils/dateUtils";
import { ensureInteger } from "../../common/utils/generalUtils";
import { deepEquals } from "../../common/utils/objectUtilities";
import { markFetched, registerEntityBasedFetch } from "../../common/hooks/initHook";

const key = roster => roster.rosterid;

const createRosterOnServer = (network, roster) => network.post('/api/v1/equipment_roster', roster, equipmentRosterMgrSlice.actions.addOrUpdate);
const updateRosterOnServer = (network, delta) => network.put('/api/v1/equipment_roster', delta, equipmentRosterMgrSlice.actions.addOrUpdate);
const removeRosterOnServer = (network, rosterid) => network.delete('/api/v1/equipment_roster/' + encodeURIComponent(rosterid), equipmentRosterMgrSlice.actions.remove);
export const publishEquipmentRoster = createAsyncThunk(
    'equipmentRosters/publishRoster',
    async (network, { getState, dispatch, fulfillWithValue, rejectWithValue }) => {
        const state = getState();   
        const wip = state.equipmentRosters.wip;
        
        const results = await Promise.allSettled(Object.entries(wip).map(async ([internalid, delta]) => {
            switch (delta.kind) {
                case 'upsert': {
                    if (delta.data.rosterid) {
                        const toSend = { ...delta.data };
                        delete toSend.internalid;
                        await updateRosterOnServer(network, toSend);
                    } else {
                        const toSend = { ...delta.data };
                        delete toSend.shift_start;
                        delete toSend.shift_end;
                        await createRosterOnServer(network, toSend);
                    }
                    break;
                }
                case 'remove': {
                    await removeRosterOnServer(network, delta.data);
                    break;
                }
                default:
                    throw new Error('Unknown WIP kind ' + delta.kind);
            }
            return internalid;
        }));
        if (results.every(r => r.status === 'fulfilled')) {
            dispatch(equipmentRosterMgrSlice.actions.clearWIP());
            fulfillWithValue(true);
        } else {
            rejectWithValue({ processed: results.filter(r => r.status === 'fulfilled').map(r => r.value), errors: results.filter(r => r.status === 'rejected').map(r => r.reason)} )
        }
    }
);

const updateState = (state, roster) => {
    if (!state.data[key(roster)] || !deepEquals(state.data[key(roster)], roster)) {
        state.data[key(roster)] = roster;
    }
}

const removeState = (state, rosterid) => {
    delete state.data[rosterid];
}

export const equipmentRosterMgrSlice = createSlice({
    name: 'equipmentRosters',
    initialState: {
        data: {},
        wip: {}
    },
    reducers: {
        set: (state, action) => {
            const toProcess = new Set(action.payload.ids.map(id => ensureInteger(id)));
            ensureArray(action.payload.data).forEach(roster => {
                updateState(state, roster);
                toProcess.delete(key(roster));
            })
            toProcess.forEach(id => delete state.data[id]);
        },
        setForShifts: (state, action) => {
            const toRemove = new Set(Object.values(state.data).filter(r => action.payload.shiftids.includes(r.shiftid)).map(r => key(r)));
            action.payload.data.forEach(roster => {
                updateState(state, roster);
                toRemove.delete(key(roster));
            });
            toRemove.forEach(rosterid => removeState(state, rosterid));
        },
        addOrUpdate: (state, action) => {
            ensureArray(action.payload).forEach(r => {
                if (!state.data[key(r)] || !deepEquals(state.data[key(r)], r)) {
                    state.data[key(r)] = r;
                }
            });
        },
        updateShift: (state, action) => {
            ensureArray(action.payload).forEach(shift => {
                for (const roster of Object.values(state.data)) {
                    if (roster.shiftid === shift.id) {
                        roster.shift_start = shift.start;
                        roster.shift_end = shift.end;
                    }
                }
                // New rosters have shift_start and shift_end set since the derrived fields are not back from the server
                // update shift_start and shift_end where necessary
                for (const wipRoster of Object.values(state.wip)) {
                    if (wipRoster.shiftid === shift.id) {
                        if ('shift_start' in wipRoster) {
                            wipRoster.shift_start = shift.start;
                        }
                        if ('shift_end' in wipRoster) {
                            wipRoster.shift_end = shift.end;
                        }
                    }
                }
            })
        },
        remove: (state, action) => {
            ensureArray(action.payload).forEach(roster => {
                delete state.data[key(roster)];
                delete state.data[roster.internalid];
            })
        },
        addOrUpdateWIP: (state, action) => {
            const internalid = action.payload.internalid
            if (!state.wip[internalid]) {
                state.wip[internalid] = { kind: 'upsert', data: action.payload }
            } else {
                state.wip[internalid] = { kind: 'upsert', data: deepmerge(state.wip[internalid].data, action.payload) };
            }
        },
        markRemovedWIP: (state, action) => {
            if (action.payload.rosterid) {
                state.wip[action.payload.internalid] = { kind: 'remove', data: action.payload.rosterid };
            } else {
                delete state.wip[action.payload.internalid];
            }
        },
        clearWIP: (state, action) => {
            state.wip = {};
        }
    },
    extraReducers: builder => {
        builder.addCase(logout, () => equipmentRosterMgrSlice.getInitialState());
        builder.addCase(publishEquipmentRoster.fulfilled, (state) => {
            state.wip = {};
        });
        builder.addCase(publishEquipmentRoster.rejected, (state, action) => {
            action.payload.processed?.forEach(internalid => delete state.wip[internalid]);
        })
    }
})

// #region network operations
const visualizeSuccess = (verb, action) => DefaultSuccessAction(verb, 'equipmentRoster', action);
const visualizeFailure = verb => DefaultFailureAction(verb, 'equipmentRoster');

const fetchRoster = aggregatingFetch(
    id => network => network.get('/api/v2/equipment_roster/' + encodeURIComponent(id), data => equipmentRosterMgrSlice.actions.set({ ids: [id], data }), visualizeFailure('get')),
    ids => network => network.post('/api/v2/equipment_rosters', ids, data => equipmentRosterMgrSlice.actions.set({ ids, data }), visualizeFailure('get')),
    { primitiveKey: true }
)

const fetchShiftRosters = aggregatingFetch(
    shiftid => network => network.get('/api/v2/equipmentRostersForShift/' + encodeURIComponent(shiftid), data => equipmentRosterMgrSlice.actions.setForShifts({ shiftids: [shiftid], data }), visualizeFailure('get')),
    shiftids => network => network.post('/api/v2/equipmentRostersForShifts', shiftids, data => equipmentRosterMgrSlice.actions.setForShifts({ shiftids, data }), visualizeFailure('get')),
    { primitiveKey: true }
)

const fetchEquipmentRostersInRange = (equipmentid, start, end) => network => network.get(`/api/v1/equipment_roster/equipment/${encodeURIComponent(equipmentid)}/${encodeURIComponent(start)}/${encodeURIComponent(end)}`, equipmentRosterMgrSlice.actions.addOrUpdate, visualizeFailure('get'));

const fetchRostersInRange = aggregatingFetch(
    (start, end) => network => network.get(`/api/v1/equipment_rosters/${encodeURIComponent(start)}/${encodeURIComponent(end)}`, equipmentRosterMgrSlice.actions.addOrUpdate, visualizeFailure('get')),
    keys => network => network.get(`/api/v1/equipment_rosters/${encodeURIComponent(minDate(keys, 0))}/${encodeURIComponent(maxDate(keys, 1))}`, equipmentRosterMgrSlice.actions.addOrUpdate, visualizeFailure('get'))
);

export const createEquipmentRoster = (dispatch, network, roster, shift) => dispatch(equipmentRosterMgrSlice.actions.addOrUpdateWIP({ shift_start: shift.start, shift_end: shift.end, internalid: crypto.randomUUID(), ...roster }));
export const updateEquipmentRoster = (dispatch, network, roster, shift) => dispatch(equipmentRosterMgrSlice.actions.addOrUpdateWIP(roster));
export const removeEquipmentRoster = (dispatch, network, roster) => dispatch(equipmentRosterMgrSlice.actions.markRemovedWIP(roster));

export const clearWIPEquipmentRoster = (dispatch, network) => dispatch(equipmentRosterMgrSlice.actions.clearWIP({}));
// #endregion

// #region selectors
const selectRawRosters = state => state.equipmentRosters.data;
const selectDataByShift = createMapSelector(
    [selectRawRosters],
    (rosters) => {
        return Object.values(rosters).reduce((res, r) => {
            if (!res[r.shiftid]) {
                res[r.shiftid] = [];
            }
            res[r.shiftid].push(r);
            return res;
        }, {});
    }
);
const selectRawWIP = state => state.equipmentRosters.wip;
const mergeWip = (rosters, wip) => {
    const wipKeys = Object.keys(wip);
    if (wipKeys.length === 0) {
        return rosters
    }
    wipKeys.forEach(internalid => {
        const wipEntry = wip[internalid];
        const idx = rosters.findIndex(r => r.internalid === internalid);
        switch (wipEntry.kind) {
            case 'remove': {
                rosters.splice(idx, 1);
                break;
            }
            case 'upsert': {
                if (idx >= 0) {
                    rosters.splice(idx, 1, deepmerge(JSON.parse(JSON.stringify(rosters[idx])), wipEntry.data));
                } else {
                    rosters.push(wipEntry.data);
                }
                break;
            }
            default:
                throw new Error('Unknown wipEntry kind ' + wipEntry.kind);
        }
    });
    return rosters;
}

const selectMergedRosters = createSelector([selectRawRosters, selectRawWIP], (rosters, wip) => {
    return mergeWip(Object.values(rosters), wip);
});

const selectRoster = field => value => {
    return createSelector([selectMergedRosters], (rosters) => {
        return rosters?.find(r => r[field] === value);
    });
}
const selectRosterByRosterid = selectRoster('rosterid');
const selectRosterByInternalid = selectRoster('internalid');
const selectRosters = field => values => {
    return createSelector([selectMergedRosters], rosters => {
        return ensureArray(values).reduce((res, value) => {
            const r = rosters?.find(r => r[field] === value);
            if (r) {
                res.push(r);
            }
            return res;
        }, []);
    })
}
const selectRostersByInternalid = selectRosters('internalid');


const selectShiftRosters = (shiftidsInput) => {
    const shiftids = ensureArray(shiftidsInput); // normalize once for consistency

    return createSelector(
        [selectDataByShift, state => state.rosters.wip],
        (shiftRosters, wip) => {
            const rosters = shiftids.flatMap(shiftid => shiftRosters[shiftid] ?? [])
            return mergeWip(
                rosters,
                wip
            );
        }
    );
};
const selectEquipmentRostersInRange = (equipmentid, start, end) => createSelector([selectMergedRosters], rosters => rosters?.filter(r => r.equipmentid === equipmentid && dateRangeOverlap({ start: r.start ?? r.shift_start, end: r.end ?? r.shift_end }, { start, end })) ?? []);
const selectRostersInRange = (start, end) => createSelector([selectMergedRosters], rosters => rosters?.filter(r => dateRangeOverlap({ start: r.start ?? r.shift_start, end: r.end ?? r.shift_end }, { start, end })) ?? []);
// #endregion

const listen = {
    equipmentRosterAdminCreated: (roster, { dispatch, websocket }) => {
        markFetched(websocket, 'equipment_roster', key(roster))
        dispatch(equipmentRosterMgrSlice.actions.addOrUpdate(roster))
    },
    equipmentRosterAdminUpdated: (roster, { dispatch }) => dispatch(equipmentRosterMgrSlice.actions.addOrUpdate(roster)),
    equipmentRosterAdminRemoved: (roster, { dispatch }) => dispatch(equipmentRosterMgrSlice.actions.remove(roster)),
    shiftAdminUpdated: (shift, { dispatch }) => dispatch(equipmentRosterMgrSlice.actions.updateShift(shift))
}

// true: event with a template.id requires recurring access roles
// false: event with no template.id requires adhoc access roles
const rosterAccess = new Map([
    ['fullRoles', new Map([
        [true, ['equipment manager', 'administrator']],
        [false, ['equipment manager', 'administrator']]
    ])],
    ['fullReadonlyRoles', new Map([
        [true, ['equipment auditor', 'auditor']],
        [false, ['equipment auditor', 'auditor']]
    ])],
    ['readonlyRoles', new Map([
        [true, ['recurring rostering auditor', 'auditor', 'equipment auditor', 'recurring rostering manager']],
        [false, ['rostering auditor', 'auditor', 'equipment auditor', 'rostering manager']]
    ])]
]);

const checkPermissions = (credentials, requiredRoles, event ) => {
   return credentials.hasRole(requiredRoles, { office: event?.office, client: event?.client, location: event?.locationid })
};

export const canViewEquipmentRosters = (credentials, event) => checkPermissions(credentials, rosterAccess.get('fullRoles').get(!!event?.templateid).concat(rosterAccess.get('readonlyRoles').get(!!event?.templateid)), event);
export const hasFullEquipmentRosterAccess = (credentials) => checkPermissions(credentials, Array.from(rosterAccess.get('fullRoles').values()).flat());
export const canUpdateEquipmentRosters = (credentials, event) => checkPermissions(credentials, rosterAccess.get('fullRoles').get(!!event?.templateid), event);

export const canMgrViewEquipmentRosters = credentials => {
    const fullRoles = rosterAccess.get('fullRoles').get(true).concat(rosterAccess.get('fullRoles').get(false));
    const readRoles = rosterAccess.get('readonlyRoles').get(true).concat(rosterAccess.get('readonlyRoles').get(false));
    return credentials.hasRole(fullRoles.concat(readRoles));
}

export const adhocFullRoles = ['administrator', 'rostering manager', 'equipment manager'];
export const adhocReadRoles = ['auditor', 'rostering auditor', 'equipment auditor', 'roster viewer','timesheet manager','timesheet auditor'];
export const templateFullRoles = ['administrator', 'recurring rostering manager', 'equipment manager'];
export const templateReadRoles = ['auditor', 'recurring rostering auditor', 'equipment auditor', 'roster viewer','timesheet manager','timesheet auditor'];
const options = {
    cleanup: { root: equipmentRosterMgrSlice.name, all: selectMergedRosters, removeAll: equipmentRosterMgrSlice.actions.remove, key },
    condition: canMgrViewEquipmentRosters
}

const rosterInitByID = initMapFactory(fetchRoster, listen, { name: 'rosterByID' });
registerEntityBasedFetch('equipment_roster', rosterInitByID);

const [useMgrEquipmentRosterByRosterID] = dataHookWithCredentialsFactory(selectRosterByRosterid, rosterInitByID, { ...options, defaultValue: null });
const [useMgrEquipmentRostersByRosterID] = dataHookWithCredentialsFactory(selectRosterByRosterid, initMultiMapFactory(rosterInitByID, listen), options);

const rosterInitByShift = queryMapFactory(fetchShiftRosters, 'rosterid', rosterInitByID, listen);
registerEntityBasedFetch('shift', rosterInitByShift);

export const [useMgrEquipmentRostersForShift, realizeMgrEquipmentRostersForShift] = dataHookWithCredentialsFactory(selectShiftRosters, rosterInitByShift, options);
export const [useMgrEquipmentRostersForShifts] = dataHookWithCredentialsFactory(selectShiftRosters, initMultiMapFactory(rosterInitByShift, listen), options);
export const realizeMgrEquipmentRostersForShifts = async (context, ids) => (await Promise.all(ids.map(id => realizeMgrEquipmentRostersForShift(context, id)))).flat();

export const [useMgrEquipmentRostersForEquipmentInRange] = dataHookWithCredentialsFactory(selectEquipmentRostersInRange, queryMapFactory(fetchEquipmentRostersInRange, 'rosterid', rosterInitByID, listen), options);
export const [useMgrEquipmentRostersInRange] = dataHookWithCredentialsFactory(selectRostersInRange, queryMapFactory(fetchRostersInRange, 'rosterid', rosterInitByID, listen), options);

export const useMgrEquipmentRoster = internalid => {
    const roster = useSelector(selectRosterByInternalid(internalid));
    useMgrEquipmentRosterByRosterID(roster?.rosterid);
    return roster;
}
export const useMgrEquipmentRosters = internalids => {
    const rosters = useSelector(selectRostersByInternalid(internalids));
    const rosterids = useMemo(() => rosters.map(r => r.rosterid).filter(id => id), [rosters]);
    useMgrEquipmentRostersByRosterID(rosterids);
    return rosters;
}

const reducer = { name: equipmentRosterMgrSlice.name, reducer: equipmentRosterMgrSlice.reducer }
export default reducer;